import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Plus, Route as RouteIcon } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/common/PageHeader';
import { DataTable } from '@/components/common/DataTable';
import { Mono } from '@/components/common/display';
import { NotImplementedState, LoadingRows } from '@/components/common/Feedback';
import { CreateVrfDialog } from '@/components/resource/CreateVrfDialog';
import { useStubResource, isNotImplemented } from '@/hooks/use-resource';

interface VrfItem {
  id: string;
  name: string;
  rd: string;
  rt_import: string;
  rt_export: string;
  description: string;
}

const COLUMNS: ColumnDef<VrfItem>[] = [
  {
    accessorKey: 'name',
    header: 'Nombre',
    cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
  },
  {
    accessorKey: 'rd',
    header: 'RD',
    cell: ({ row }) =>
      row.original.rd ? (
        <Mono className="text-xs">{row.original.rd}</Mono>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    accessorKey: 'rt_import',
    header: 'RT Import',
    cell: ({ row }) =>
      row.original.rt_import ? (
        <Mono className="text-xs">{row.original.rt_import}</Mono>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    accessorKey: 'rt_export',
    header: 'RT Export',
    cell: ({ row }) =>
      row.original.rt_export ? (
        <Mono className="text-xs">{row.original.rt_export}</Mono>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    accessorKey: 'description',
    header: 'Descripción',
    cell: ({ row }) =>
      row.original.description ? (
        <span className="text-xs text-muted-foreground">{row.original.description}</span>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
];

export const Route = createFileRoute('/_auth/vrfs')({
  component: VrfsPage,
});

function VrfsPage() {
  const { data, isLoading, error } = useStubResource<VrfItem>('vrfs');
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="VRFs"
        icon={RouteIcon}
        description="Instancias de enrutamiento virtual (Virtual Routing and Forwarding)"
        actions={
          <Button size="sm" className="h-7 text-xs" onClick={() => setCreateOpen(true)}>
            <Plus className="mr-1 size-3.5" />
            Nueva VRF
          </Button>
        }
      />
      <CreateVrfDialog open={createOpen} onOpenChange={setCreateOpen} />

      <div className="flex min-h-0 flex-1 flex-col p-4">
        {isLoading ? (
          <div className="space-y-2 p-2">
            <LoadingRows rows={8} />
          </div>
        ) : isNotImplemented(error) ? (
          <NotImplementedState resource="VRFs" />
        ) : (
          <DataTable
            columns={COLUMNS}
            data={data?.data ?? []}
            getRowId={(r) => r.id}
            error={!isNotImplemented(error) ? error : undefined}
            emptyTitle="Sin VRFs"
            emptyDescription="No hay instancias VRF registradas."
            csvFilename="vrfs"
          />
        )}
      </div>
    </div>
  );
}
